import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "@/components/layout/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/authContext";
import { getCapacityReport, CapacityReportRow } from "@/lib/api";
import { Loader2, ShieldAlert, ChevronLeft, ChevronRight } from "lucide-react";
import { format, parseISO, addWeeks, subWeeks, startOfWeek, subDays } from "date-fns";

function getMondayOfWeek(date: Date): string {
  const monday = startOfWeek(date, { weekStartsOn: 1 });
  return format(monday, "yyyy-MM-dd");
}

export default function ReportCapacity() {
  const { user } = useAuth();
  const isManager = user?.role === "manager" || user?.role === "admin";

  const [weekOf, setWeekOf] = useState<string>(getMondayOfWeek(new Date()));

  const { data, isLoading, error } = useQuery({
    queryKey: ["reports-capacity", weekOf],
    queryFn: () => getCapacityReport(weekOf),
    enabled: isManager,
  });

  const rows: CapacityReportRow[] = data?.rows ?? [];
  const weekStart = data?.weekStart ?? weekOf;
  const weekEnd = data?.weekEnd;

  const weekLabel = weekStart && weekEnd
    ? `${format(parseISO(weekStart), "MMM d")} – ${format(parseISO(weekEnd), "MMM d, yyyy")}`
    : weekOf;

  function prevWeek() {
    setWeekOf(format(subWeeks(parseISO(weekOf), 1), "yyyy-MM-dd"));
  }

  function nextWeek() {
    setWeekOf(format(addWeeks(parseISO(weekOf), 1), "yyyy-MM-dd"));
  }

  function currentWeek() {
    setWeekOf(getMondayOfWeek(new Date()));
  }

  function statusBadge(row: CapacityReportRow) {
    const pct = row.utilizationPercent;
    if (pct > 100) {
      return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-0">Over</Badge>;
    }
    if (pct >= 80) {
      return <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 border-0">On Track</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-0">Under</Badge>;
  }

  function pctBarColor(pct: number) {
    if (pct > 100) return "bg-red-500";
    if (pct >= 80) return "bg-emerald-500";
    return "bg-amber-400";
  }

  if (!isManager) {
    return (
      <Layout>
        <div className="space-y-6">
          <h1 className="text-3xl font-heading font-bold">Capacity Report</h1>
          <Card className="border-dashed border-2 border-destructive/50">
            <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <ShieldAlert className="w-12 h-12 mb-4 text-destructive/50" />
              <p className="text-lg font-medium">Access Denied</p>
              <p className="text-sm">You need manager or administrator privileges to access reports.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Capacity Report</h1>
          <p className="text-muted-foreground">Weekly hours logged vs capacity per staff member</p>
        </div>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Button variant="outline" size="icon" onClick={prevWeek} title="Previous week">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-medium min-w-[200px] text-center">{weekLabel}</span>
              <Button variant="outline" size="icon" onClick={nextWeek} title="Next week">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setWeekOf(getMondayOfWeek(subDays(new Date(), 7)))}>
                Last Week
              </Button>
              <Button variant="outline" size="sm" onClick={currentWeek}>
                This Week
              </Button>
            </div>
          </CardContent>
        </Card>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <Card>
            <CardContent className="py-12 text-center text-destructive">
              Failed to load capacity data.
            </CardContent>
          </Card>
        ) : rows.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No active staff found.
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Staff Capacity — {weekLabel}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30 text-muted-foreground text-left">
                    <th className="px-4 py-3 font-medium">Staff Name</th>
                    <th className="px-4 py-3 font-medium text-right">Capacity</th>
                    <th className="px-4 py-3 font-medium text-right">Logged</th>
                    <th className="px-4 py-3 font-medium">Utilization</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r: CapacityReportRow) => (
                    <tr key={r.userId} className="hover:bg-muted/20">
                      <td className="px-4 py-3 font-medium">{r.userName}</td>
                      <td className="px-4 py-3 text-right font-mono text-muted-foreground">
                        {r.weeklyCapacityHours}h
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold">
                        {r.loggedHours.toFixed(1)}h
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 min-w-[140px]">
                          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${pctBarColor(r.utilizationPercent)}`}
                              style={{ width: `${Math.min(r.utilizationPercent, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs font-mono w-10 text-right">
                            {r.utilizationPercent}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">{statusBadge(r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
